import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { UserPlus, Search, Key } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { UserForm } from '@/components/forms/UserForm'
import { AdminStaffTable } from '@/components/admin/AdminStaffTable'
import { PaginationControlsComponent } from '@/components/ui/pagination-controls'
import { usePagination } from '@/hooks/usePagination'
import { UserListSkeleton } from '@/components/ui/skeletons'
import { InlineLoading } from '@/components/ui/loading-spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { User } from '@/types'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
import { getStoredUserId, getStoredUserRole } from '@/lib/staff-roles'
import {
  deactivateConfirmText,
  deleteRefusalOffersDeactivate,
  staffListParams,
  staffStatusActionBlock,
} from '@/lib/staffStatus'
import { StaffAllowanceDialog, type StaffAllowanceTarget } from '@/components/admin/staff/StaffAllowanceDialog'
import { AllowanceUsage, NoLoginAllowancesCard } from '@/components/admin/staff/NoLoginAllowancesCard'

export function AdminStaffManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [pinUser, setPinUser] = useState<User | null>(null)
  const [pinValue, setPinValue] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [pinError, setPinError] = useState('')
  const [deleteUserOpen, setDeleteUserOpen] = useState(false)
  const [pendingDeleteUser, setPendingDeleteUser] = useState<User | null>(null)
  // F-CTRL-N76: a delete refused for history flips the dialog into "deactivate
  // instead" with the server's reason. Former staff are hidden from the list
  // unless asked for (the archive).
  const [deleteRefusal, setDeleteRefusal] = useState<string | null>(null)
  const [showFormer, setShowFormer] = useState(false)
  const [statusTarget, setStatusTarget] = useState<{ user: User; active: boolean } | null>(null)

  const queryClient = useQueryClient()

  // F-MM-N31 — meal allowances live here, not on customer profiles. Only an
  // admin gives or changes one, never their own (the server enforces it).
  const houseAccounts = useHouseAccountsEnabled()
  const myRole = getStoredUserRole()
  const isAdmin = myRole === 'admin'
  const myId = getStoredUserId()
  const [allowanceTarget, setAllowanceTarget] = useState<StaffAllowanceTarget | null>(null)
  const refreshAllowances = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] })
    queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
  }
  const renderMealAllowance = (user: User) => {
    const a = user.meal_allowance
    if (a) {
      return (
        <button
          type="button"
          onClick={() => setAllowanceTarget({ mode: 'view', accountId: a.account_id, cycleMonth: a.cycle_month })}
          className="-mx-2 rounded-md px-2 py-1 text-left hover:bg-muted"
          aria-label={`Meal allowance for ${user.first_name} ${user.last_name}`}
        >
          {a.status === 'suspended' ? (
            <span className="text-sm text-muted-foreground">Paused</span>
          ) : (
            <AllowanceUsage
              align="left"
              used={a.used_this_month + a.over_this_month}
              allowance={a.monthly_allowance}
              over={a.over_this_month}
            />
          )}
        </button>
      )
    }
    if (isAdmin && String(user.id) !== myId && user.is_active) {
      return (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAllowanceTarget({ mode: 'create-login', user })}>
          Give allowance
        </Button>
      )
    }
    return <span className="text-muted-foreground">—</span>
  }

  // Pagination hook
  const pagination = usePagination({ 
    initialPage: 1, 
    initialPageSize: 10,
    total: 0 
  })

  useEffect(() => {
    if (searchTerm !== debouncedSearch) {
      setIsSearching(true)
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      pagination.goToFirstPage()
      setIsSearching(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm, debouncedSearch])

  // Fetch users with pagination (full response preserves `meta` for totals)
  const { data: usersData, isLoading, isFetching } = useQuery({
    queryKey: ['users', pagination.page, pagination.pageSize, debouncedSearch, showFormer],
    queryFn: () =>
      apiClient.getUsers(
        staffListParams({
          page: pagination.page,
          limit: pagination.pageSize,
          search: debouncedSearch,
          showFormer,
        }),
      ),
  })

  // Extract data and pagination info
  const users = Array.isArray(usersData) ? usersData : (usersData as any)?.data || []
  const usersMeta = (usersData as any)?.meta ?? (usersData as any)?.pagination
  const usersTotal = typeof usersMeta?.total === 'number' ? usersMeta.total : 0

  // Delete user mutation (keep existing functionality)  
  const deleteUserMutation = useMutation({
    mutationFn: ({ id }: { id: string, username: string }) => apiClient.deleteUser(id),
    onSuccess: (_, { username: deletedUsername }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toastHelpers.userDeleted(deletedUsername)
      setDeleteUserOpen(false)
      setPendingDeleteUser(null)
    },
    onError: (error: any) => {
      if (deleteRefusalOffersDeactivate(error)) {
        // Keep the dialog open and turn it into the right action.
        setDeleteRefusal(error?.message || 'This account has history and cannot be deleted.')
        return
      }
      setDeleteUserOpen(false)
      setPendingDeleteUser(null)
      toastHelpers.apiError('Delete user', error)
    }
  })

  // F-CTRL-N76: deactivate = fire. The backend revokes every live session,
  // refuses login and the manager PIN, and pauses the meal allowance.
  const setActiveMutation = useMutation({
    mutationFn: ({ user, active }: { user: User; active: boolean }) =>
      apiClient.updateUser(String(user.id), { is_active: active }),
    onSuccess: (_, { user, active }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
      toastHelpers.success(
        active ? 'Staff member reactivated' : 'Staff member deactivated',
        active
          ? `${user.first_name} ${user.last_name} can log in again.`
          : `${user.first_name} ${user.last_name} has been signed out and can no longer log in.`,
      )
      setStatusTarget(null)
      setDeleteUserOpen(false)
      setPendingDeleteUser(null)
      setDeleteRefusal(null)
    },
    onError: (error: any) => {
      toastHelpers.apiError(statusTarget?.active ? 'Reactivate staff member' : 'Deactivate staff member', error)
    },
  })

  const statusBlock = (user: User) =>
    staffStatusActionBlock({ actorRole: myRole, actorId: myId, target: { id: String(user.id), role: user.role } })

  const handleSetActive = (user: User, active: boolean) => {
    if (statusBlock(user)) return
    setStatusTarget({ user, active })
  }

  const setPinMutation = useMutation({
    mutationFn: ({ userId, pin }: { userId: string; pin: string }) => apiClient.setUserPin(userId, pin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setPinUser(null)
      setPinValue('')
      setPinConfirm('')
      setPinError('')
    },
    onError: (error: any) => {
      setPinError(error.message || 'Failed to set PIN')
    },
  })

  const handleSetPin = () => {
    if (pinValue.length !== 4 || !/^\d{4}$/.test(pinValue)) {
      setPinError('PIN must be exactly 4 digits')
      return
    }
    if (pinValue !== pinConfirm) {
      setPinError('PINs do not match')
      return
    }
    if (!pinUser) return
    setPinMutation.mutate({ userId: pinUser.id, pin: pinValue })
  }

  const handleFormSuccess = () => {
    setShowCreateForm(false)
    setEditingUser(null)
  }

  const handleCancelForm = () => {
    setShowCreateForm(false)
    setEditingUser(null)
  }

  const handleDeleteUser = (user: User) => {
    setPendingDeleteUser(user)
    setDeleteRefusal(null)
    setDeleteUserOpen(true)
  }

  const closeDeleteDialog = () => {
    setDeleteUserOpen(false)
    setPendingDeleteUser(null)
    setDeleteRefusal(null)
  }

  const confirmDeleteUser = () => {
    if (!pendingDeleteUser) return
    const displayName = `${pendingDeleteUser.first_name} ${pendingDeleteUser.last_name}`
    deleteUserMutation.mutate({
      id: pendingDeleteUser.id.toString(),
      username: displayName,
    })
  }

  // Data is already filtered on the server side
  const filteredUsers = users

  // Show form if creating or editing
  if (showCreateForm || editingUser) {
    return (
      <div className="p-6">
        <UserForm
          user={editingUser || undefined}
          mode={editingUser ? 'edit' : 'create'}
          onSuccess={handleFormSuccess}
          onCancel={handleCancelForm}
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-72 bg-muted animate-pulse rounded-md" />
          </div>
          <div className="h-10 w-24 bg-muted animate-pulse rounded-md" />
        </div>
        
        {/* Search and Controls Skeleton */}
        <div className="flex items-center justify-between gap-4">
          <div className="h-10 w-full max-w-sm bg-muted animate-pulse rounded-md" />
        </div>
        
        {/* User List Skeleton */}
        <UserListSkeleton count={pagination.pageSize} />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Staff Management</h2>
          <p className="text-muted-foreground">
            Manage your restaurant staff and their permissions
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="gap-2 self-start sm:self-auto">
          <UserPlus className="h-4 w-4" />
          Add New Staff
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search staff by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
            {isSearching && (
              <div className="absolute right-2 top-2.5">
                <InlineLoading size="sm" />
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Switch id="show-former-staff" checked={showFormer} onCheckedChange={(v) => { setShowFormer(v); pagination.goToFirstPage() }} />
            <Label htmlFor="show-former-staff" className="text-sm text-muted-foreground">
              Show former staff
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Staff table */}
      <div className="space-y-4">
        <AdminStaffTable
          data={filteredUsers}
          onEdit={setEditingUser}
          onDelete={handleDeleteUser}
          onSetPin={setPinUser}
          onSetActive={handleSetActive}
          statusBlock={statusBlock}
          isLoading={isLoading}
          renderMealAllowance={houseAccounts.staff ? renderMealAllowance : undefined}
        />
      </div>

      {/* Pagination with loading state */}
      {filteredUsers.length > 0 && (
        <div className="mt-6 space-y-4">
          {isFetching && !isLoading && (
            <div className="flex justify-center">
              <InlineLoading text="Updating results..." />
            </div>
          )}
          <PaginationControlsComponent
            pagination={pagination}
            total={usersTotal}
          />
        </div>
      )}

      {houseAccounts.staff && (
        <NoLoginAllowancesCard
          canAdd={isAdmin}
          onAdd={() => setAllowanceTarget({ mode: 'create-person' })}
          onOpen={(a) => setAllowanceTarget({ mode: 'view', accountId: a.id, cycleMonth: a.cycle_month })}
        />
      )}

      <StaffAllowanceDialog
        target={allowanceTarget}
        onClose={() => setAllowanceTarget(null)}
        onChanged={refreshAllowances}
      />

      {/*
        * Set-Manager-PIN. This was a hand-rolled `fixed inset-0` overlay that
        * bypassed the Dialog primitive — which is how it ended up as a
        * hardcoded white card with near-black ink, i.e. a glaring white block
        * on the dark canvas (F-FE-N55). Moving it onto Dialog fixes the theme
        * permanently and picks up the focus trap, Esc-to-close and scroll lock
        * it never had.
        */}
      <Dialog
        open={!!pinUser}
        onOpenChange={(open) => {
          if (!open) { setPinUser(null); setPinValue(''); setPinConfirm(''); setPinError('') }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-info-fg" />
              Set Manager PIN
            </DialogTitle>
            <DialogDescription>
              Setting PIN for{' '}
              <span className="font-semibold text-foreground">
                {pinUser?.first_name} {pinUser?.last_name}
              </span>{' '}
              ({pinUser?.role})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">4-Digit PIN</label>
              <Input
                type="password"
                maxLength={4}
                placeholder="Enter 4-digit PIN"
                value={pinValue}
                onChange={e => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError('') }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Confirm PIN</label>
              <Input
                type="password"
                maxLength={4}
                placeholder="Re-enter PIN"
                value={pinConfirm}
                onChange={e => { setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError('') }}
              />
            </div>
            {pinError && <p className="text-danger-fg text-sm">{pinError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { setPinUser(null); setPinValue(''); setPinConfirm(''); setPinError('') }}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleSetPin} disabled={setPinMutation.isPending}>
              {setPinMutation.isPending ? 'Saving...' : 'Save PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteUserOpen} onOpenChange={(open) => { if (!open) closeDeleteDialog() }}>
        <DialogContent className="sm:max-w-md">
          {deleteRefusal && pendingDeleteUser ? (
            <>
              <DialogHeader>
                <DialogTitle>Can’t delete — deactivate instead?</DialogTitle>
                <DialogDescription className="space-y-2">
                  <span className="block">{deleteRefusal}</span>
                  {pendingDeleteUser.is_active ? (
                    <span className="block">{deactivateConfirmText(pendingDeleteUser.first_name, pendingDeleteUser.last_name)}</span>
                  ) : (
                    <span className="block">{pendingDeleteUser.first_name} {pendingDeleteUser.last_name} is already deactivated and cannot log in.</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={closeDeleteDialog}>
                  Close
                </Button>
                {pendingDeleteUser.is_active && (
                  <Button
                    onClick={() => setActiveMutation.mutate({ user: pendingDeleteUser, active: false })}
                    disabled={setActiveMutation.isPending || !!statusBlock(pendingDeleteUser)}
                    title={statusBlock(pendingDeleteUser) ?? undefined}
                  >
                    {setActiveMutation.isPending ? 'Deactivating…' : 'Deactivate'}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Delete Staff Member?</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete {pendingDeleteUser?.first_name} {pendingDeleteUser?.last_name}? This action cannot be undone.
                  Deleting only works for accounts with no history; to let someone go, use Deactivate.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={closeDeleteDialog}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDeleteUser} disabled={deleteUserMutation.isPending}>
                  {deleteUserMutation.isPending ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusTarget} onOpenChange={(open) => { if (!open) setStatusTarget(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusTarget?.active ? 'Reactivate' : 'Deactivate'} {statusTarget?.user.first_name} {statusTarget?.user.last_name}?
            </DialogTitle>
            <DialogDescription>
              {statusTarget && (statusTarget.active
                ? `${statusTarget.user.first_name} ${statusTarget.user.last_name} will be able to log in again with their existing password and PIN. A paused meal allowance is not restarted — do that from the allowance.`
                : deactivateConfirmText(statusTarget.user.first_name, statusTarget.user.last_name))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setStatusTarget(null)}>
              Cancel
            </Button>
            <Button
              variant={statusTarget?.active ? 'default' : 'destructive'}
              disabled={setActiveMutation.isPending}
              onClick={() => statusTarget && setActiveMutation.mutate(statusTarget)}
            >
              {setActiveMutation.isPending
                ? (statusTarget?.active ? 'Reactivating…' : 'Deactivating…')
                : (statusTarget?.active ? 'Reactivate' : 'Deactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}